import type { JobUpsertParams } from '../types/job';

export interface PublicationInput {
  jobId: string;
  listingTypeId: number;
  expiresAt: string;
  selectionDate: string | null;
  publicationRequestId: string;
}

export interface PublicationResult {
  jobId: string;
  status: 'published' | 'already_processed' | 'draft_saved';
}

export interface JobCreationDependencies {
  upsertDraft: (params: JobUpsertParams, jobId?: string) => Promise<{ jobId: string }>;
  onDraftSaved?: (jobId: string) => void;
  publish: (input: PublicationInput) => Promise<'published' | 'already_processed'>;
  adminPublish: (input: Omit<PublicationInput, 'publicationRequestId'>) => Promise<void>;
  /**
   * Admin publication has no server-side request key. If its response is
   * lost, inspect the persisted job before treating the action as failed.
   */
  reconcileAdminPublication?: (
    input: Omit<PublicationInput, 'publicationRequestId'>
  ) => Promise<boolean>;
  refresh: () => Promise<void>;
}

/**
 * Orchestrates stores supplied by the UI. Keeping Supabase out of this module
 * makes the retry policy explicit and straightforward to test.
 */
export const createJobCreationService = (dependencies: JobCreationDependencies) => ({
  async saveDraft(params: JobUpsertParams, existingJobId?: string): Promise<PublicationResult> {
    const { jobId } = await dependencies.upsertDraft(params, existingJobId);
    dependencies.onDraftSaved?.(jobId);
    await dependencies.refresh();
    return { jobId, status: 'draft_saved' };
  },

  async publish(
    params: JobUpsertParams,
    input: Omit<PublicationInput, 'jobId'>,
    isShadowRecruiter: boolean,
    existingJobId?: string
  ): Promise<PublicationResult> {
    const { jobId } = await dependencies.upsertDraft(params, existingJobId);
    dependencies.onDraftSaved?.(jobId);
    if (isShadowRecruiter) {
      const adminInput = {
        jobId,
        listingTypeId: input.listingTypeId,
        expiresAt: input.expiresAt,
        selectionDate: input.selectionDate,
      };
      try {
        await dependencies.adminPublish(adminInput);
      } catch (error) {
        // Do not retry the RPC: it might already have committed. This also
        // makes a restored session safe after a lost response, where the
        // second RPC call returns JOB_ALREADY_PUBLISHED.
        let published = false;
        try {
          published = (await dependencies.reconcileAdminPublication?.(adminInput)) ?? false;
        } catch {
          // The original mutation error is more useful if reconciliation is
          // unavailable too.
        }
        if (!published) throw error;
      }
      await dependencies.refresh();
      return { jobId, status: 'published' };
    }
    const status = await dependencies.publish({ ...input, jobId });
    await dependencies.refresh();
    return { jobId, status };
  },
});
